import Link from "next/link";
import Image from "next/image";
import parse, { attributesToProps, domToReact, type DOMNode } from "html-react-parser";

import { track } from "@/lib/analytics";
import { ghostImage, type BlogPost } from "@/lib/ghost";

const stripRef = (href: string) => {
  const hashIndex = href.indexOf("#");
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
  const withoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const queryIndex = withoutHash.indexOf("?");
  if (queryIndex === -1) return href;

  const base = withoutHash.slice(0, queryIndex);
  const kept = withoutHash
    .slice(queryIndex + 1)
    .split("&")
    .filter((part) => part !== "" && part !== "ref=crayo-ai.ghost.io");

  return `${base}${kept.length ? `?${kept.join("&")}` : ""}${hash}`;
};

const internalBlogPath = (href: string) => {
  if (href.startsWith("/blog/") || href === "/blog") return href;
  if (href.startsWith("https://crayo.ai/blog")) return href.slice("https://crayo.ai".length);
  return null;
};

const TABLE_TAGS = new Set(["table", "thead", "tbody", "tfoot", "tr"]);

const htmlParseOptions = {
  replace: (domNode: DOMNode) => {
    if (domNode.type === "text") {
      const parent = domNode.parent;
      const inTable = parent && parent.type === "tag" && TABLE_TAGS.has(parent.name);
      return inTable && domNode.data.trim() === "" ? <></> : undefined;
    }

    if (domNode.type !== "tag") return;

    const { name, children } = domNode;
    const jsx = domToReact(children as DOMNode[], htmlParseOptions);

    switch (name) {
      case "p":
        return <p className="text-[15px] leading-relaxed text-gray-800">{jsx}</p>;
      case "h2":
        return <h2 className="text-2xl font-semibold leading-relaxed">{jsx}</h2>;
      case "h3":
        return <h3 className="text-xl font-semibold leading-relaxed">{jsx}</h3>;
      case "h4":
        return <h4 className="text-base font-semibold">{jsx}</h4>;
      case "li":
        return <li className="list-inside list-disc text-[15px] text-gray-800">{jsx}</li>;
      case "img": {
        const props = attributesToProps(domNode.attribs);
        return (
          <img {...props} loading="lazy" alt={typeof props.alt === "string" ? props.alt : ""} />
        );
      }
      case "table":
        return (
          <div className="w-full overflow-x-auto">
            <table {...attributesToProps(domNode.attribs)}>{jsx}</table>
          </div>
        );
      case "a": {
        const href = stripRef(domNode.attribs.href ?? "");
        const blogPath = internalBlogPath(href);
        const className = "text-[15px] text-blue-500 hover:underline";

        if (blogPath) {
          return (
            <Link href={blogPath} className={className}>
              {jsx}
            </Link>
          );
        }

        const isConversionLink = href.includes("crayo.ai") && !href.includes("/blog");
        return (
          <a
            href={href}
            onClick={
              isConversionLink
                ? () => track("cta_clicked", { cta: "blog_content", href })
                : undefined
            }
            className={className}
          >
            {jsx}
          </a>
        );
      }
      default:
        return;
    }
  },
};

export const BlogPostContent = ({ post }: { post: BlogPost }) => (
  <article className="mx-auto w-full max-w-screen-lg space-y-10 px-5 pb-10 pt-2 text-base text-gray-700">
    {post.feature_image && (
      <div className="relative mx-auto aspect-video w-full max-w-2xl overflow-hidden rounded-xl">
        <Image
          priority
          className="object-cover"
          src={ghostImage(post.feature_image, 1200)}
          alt={post.feature_image_alt || post.title}
          fill
          sizes="(max-width: 672px) 100vw, 672px"
        />
      </div>
    )}
    <div className="mx-auto w-full max-w-xl space-y-5">{parse(post.html, htmlParseOptions)}</div>
  </article>
);
